import React, { useMemo } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  useTheme,
} from "@mui/material";
import { LineChart } from "../../../../../../shared/components/charts";
import {
  useReportWeather,
  useReportWeatherAnomalies,
} from "../../hooks/useReportData";
import type { WeatherImpactBlock } from "../../api/types";
import type {
  WeatherTrendPoint,
  WeatherAnomaly,
  WeatherAnomalySeverity,
} from "../../../../../../shared/api/weather";

interface WeatherImpactViewProps {
  block: WeatherImpactBlock;
  clientId: string | null;
  branding?: { primaryColor?: string; chartColor?: string };
  fill?: boolean;
}

const severityToAlert = (
  severity: WeatherAnomalySeverity,
): "info" | "warning" | "error" => {
  switch (severity) {
    case "severe":
      return "error";
    case "warning":
      return "warning";
    default:
      return "info";
  }
};

/**
 * Weather vs wellbeing overlay. Plots the weather trend (temperature /
 * precipitation) over the period and lists anomaly callouts as alerts. Robust
 * to missing trend or anomaly data.
 */
export const WeatherImpactView: React.FC<WeatherImpactViewProps> = ({
  block,
  clientId,
  branding,
  fill = false,
}) => {
  const theme = useTheme();
  const tempColor =
    branding?.chartColor ?? branding?.primaryColor ?? theme.palette.primary.main;
  const trendsQuery = useReportWeather(block.filters, clientId, block.ukRegion);
  const anomaliesQuery = useReportWeatherAnomalies(block.filters, clientId, block.ukRegion);

  const trends: WeatherTrendPoint[] = trendsQuery.data?.data?.trends ?? [];
  const anomalies: WeatherAnomaly[] = anomaliesQuery.data?.data?.anomalies ?? [];

  const chartData = useMemo(
    () =>
      trends.map((t) => ({
        label: t.label,
        temperature: t.temperature,
        precipitation: t.precipitation,
      })),
    [trends],
  );

  if (trendsQuery.isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (trendsQuery.isError) {
    return <Alert severity="error">Couldn't load weather data.</Alert>;
  }

  if (chartData.length === 0) {
    return <Alert severity="info">No weather data for this period.</Alert>;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {block.title && (
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
          {block.title}
        </Typography>
      )}

      <LineChart
        data={chartData}
        xAxisKey="label"
        series={[
          {
            dataKey: "temperature",
            label: "Temperature (°C)",
            color: tempColor,
            area: true,
          },
          {
            dataKey: "precipitation",
            label: "Precipitation (mm)",
            color: theme.palette.info.main,
          },
        ]}
        height={fill ? 420 : 320}
      />

      {anomalies.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Weather anomalies
          </Typography>
          {anomalies.map((a, i) => (
            <Alert key={`${a.kind}-${i}`} severity={severityToAlert(a.severity)}>
              <strong>{a.label}</strong> — {a.startDay}
              {a.endDay && a.endDay !== a.startDay ? ` to ${a.endDay}` : ""}
              {a.isForecast ? " (forecast)" : ""}
            </Alert>
          ))}
        </Stack>
      )}
    </Box>
  );
};
